import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { useUIVersion } from '@/utils/UIVersionContext'
import Cookies from 'js-cookie'
import { ArrowLeft, ArrowRight, Moon, PanelLeft, Sparkles, X } from 'lucide-react'

const TOUR_COOKIE = 'tickmill_tour_seen'

interface TourStep {
  selector: string
  title: string
  description: string
  icon: React.ReactNode
  position: 'top' | 'bottom' | 'left' | 'right'
}

const STEPS: TourStep[] = [
  {
    selector: '[data-test="my-account-button"]',
    title: 'Your Profile',
    description:
      'Click your name to access Profile Settings where you can enable the new Sidebar Navigation.',
    icon: <Sparkles className='h-5 w-5' />,
    position: 'bottom',
  },
  {
    selector: '[data-test="records-menu-link"]',
    title: 'New Navigation Design',
    description:
      'The navigation has been refreshed with modern icons and styling. Switch to Sidebar mode for an even better experience.',
    icon: <PanelLeft className='h-5 w-5' />,
    position: 'bottom',
  },
  {
    selector: '[data-test="records-menu-link"]',
    title: 'Dark Mode & More',
    description:
      'Enable the new UI to unlock Dark Mode, improved forms, and a clean modern interface throughout.',
    icon: <Moon className='h-5 w-5' />,
    position: 'bottom',
  },
]

interface SpotlightRect {
  top: number
  left: number
  width: number
  height: number
}

export const TourGuide: React.FC = () => {
  const { isNewUI, enableNewUI } = useUIVersion()
  const [active, setActive] = useState(false)
  const [step, setStep] = useState(0)
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isNewUI && !Cookies.get(TOUR_COOKIE)) {
      const timer = setTimeout(() => setActive(true), 800)
      return () => clearTimeout(timer)
    }
  }, [isNewUI])

  const positionTooltip = useCallback(() => {
    const currentStep = STEPS[step]
    if (!currentStep) return

    const el = document.querySelector(currentStep.selector)
    if (!el) return

    const rect = el.getBoundingClientRect()
    const padding = 8

    setSpotlight({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    })

    const tooltipWidth = 340
    const tooltipHeight = 200
    const gap = 16

    let top = 0
    let left = 0

    switch (currentStep.position) {
      case 'bottom':
        top = rect.bottom + gap
        left = rect.left + rect.width / 2 - tooltipWidth / 2
        break
      case 'top':
        top = rect.top - tooltipHeight - gap
        left = rect.left + rect.width / 2 - tooltipWidth / 2
        break
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2
        left = rect.left - tooltipWidth - gap
        break
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2
        left = rect.right + gap
        break
    }

    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16))
    top = Math.max(16, top)

    setTooltipPos({ top, left })
  }, [step])

  useEffect(() => {
    if (!active) return
    positionTooltip()

    const handleResize = () => positionTooltip()
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [active, step, positionTooltip])

  const dismiss = () => {
    Cookies.set(TOUR_COOKIE, '1', { expires: 365 })
    setActive(false)
  }

  const trySidebar = () => {
    Cookies.set(TOUR_COOKIE, '1', { expires: 365 })
    enableNewUI()
    setActive(false)
  }

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1)
  }

  const prev = () => {
    if (step > 0) setStep(step - 1)
  }

  if (!active || isNewUI) return null

  const currentStep = STEPS[step]
  const isLast = step === STEPS.length - 1

  return createPortal(
    <>
      <div
        className='fixed inset-0 z-[9998] transition-opacity duration-300'
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          maskImage: spotlight
            ? `radial-gradient(ellipse at ${spotlight.left + spotlight.width / 2}px ${spotlight.top + spotlight.height / 2}px, transparent ${Math.max(spotlight.width, spotlight.height) * 0.6}px, black ${Math.max(spotlight.width, spotlight.height) * 0.8}px)`
            : undefined,
          WebkitMaskImage: spotlight
            ? `radial-gradient(ellipse at ${spotlight.left + spotlight.width / 2}px ${spotlight.top + spotlight.height / 2}px, transparent ${Math.max(spotlight.width, spotlight.height) * 0.6}px, black ${Math.max(spotlight.width, spotlight.height) * 0.8}px)`
            : undefined,
        }}
        onClick={dismiss}
      />

      {spotlight && (
        <div
          className='pointer-events-none fixed z-[9999] rounded-lg ring-2 ring-white/40 transition-all duration-300'
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
        />
      )}

      <div
        ref={tooltipRef}
        className='fixed z-[10000] w-[340px] animate-in fade-in-0 zoom-in-95 rounded-xl border border-border bg-background p-5 shadow-2xl transition-all duration-300'
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <button
          onClick={dismiss}
          className='absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground'
        >
          <X className='h-4 w-4' />
        </button>

        <div className='mb-3 flex items-center gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            {currentStep.icon}
          </div>
          <div>
            <h3 className='text-sm font-semibold text-foreground'>{currentStep.title}</h3>
            <span className='text-[11px] text-muted-foreground'>
              Step {step + 1} of {STEPS.length}
            </span>
          </div>
        </div>

        <p className='mb-4 text-sm leading-relaxed text-muted-foreground'>
          {currentStep.description}
        </p>

        <div className='mb-4 flex justify-center gap-1.5'>
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        <div className='flex items-center justify-between'>
          <div>
            {step > 0 && (
              <Button variant='ghost' size='sm' onClick={prev}>
                <ArrowLeft className='mr-1 h-3.5 w-3.5' />
                Back
              </Button>
            )}
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={dismiss}>
              Skip
            </Button>
            {isLast ? (
              <Button size='sm' onClick={trySidebar}>
                Try Sidebar
                <ArrowRight className='ml-1 h-3.5 w-3.5' />
              </Button>
            ) : (
              <Button size='sm' onClick={next}>
                Next
                <ArrowRight className='ml-1 h-3.5 w-3.5' />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
